import { useEffect, useMemo, useState } from "react";
import {
  CircleDollarSign,
  Gauge,
  LoaderCircle,
  LogOut,
  Percent,
  Target,
  Trophy,
  Zap,
} from "lucide-react";
import { Brand } from "./Brand";
import { Filters } from "./Filters";
import { MetricCard } from "./MetricCard";
import { PerformanceChart } from "./PerformanceChart";
import { createPool, loadDirectory, loadSummary } from "../lib/repository";
import { isSupabaseConfigured, supabase } from "../lib/supabase";
import type { DashboardSummary, DateRange, Player, SavedPool } from "../types";

const emptySummary: DashboardSummary = {
  roi: 0,
  profitUsd: 0,
  tournaments: 0,
  itm: 0,
  abi: 0,
  usdPerHour: null,
  chart: [],
};

const today = new Date().toISOString().slice(0, 10);
const initialRange: DateRange = isSupabaseConfigured
  ? { from: `${today.slice(0, 4)}-01-01`, to: today }
  : { from: "2026-01-01", to: "2026-07-26" };

const percentage = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const integer = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 });
const usd = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function Dashboard() {
  const [range, setRange] = useState<DateRange>(initialRange);
  const [players, setPlayers] = useState<Player[]>([]);
  const [pools, setPools] = useState<SavedPool[]>([]);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<string[]>([]);
  const [activePoolId, setActivePoolId] = useState("");
  const [summary, setSummary] = useState<DashboardSummary>(emptySummary);
  const [loading, setLoading] = useState(true);
  const [directoryLoaded, setDirectoryLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    loadDirectory()
      .then((directory) => {
        setPlayers(directory.players);
        setPools(directory.pools);
        setSelectedPlayerIds(directory.players.map((player) => player.id));
        if (directory.players.length === 0) {
          setError(
            "Nenhum jogador disponível para esta conta. O banco pode estar vazio ou o acesso ao time precisa ser conferido.",
          );
        }
      })
      .catch((reason: Error) => setError(reason.message))
      .finally(() => {
        setDirectoryLoaded(true);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!directoryLoaded || players.length === 0 || selectedPlayerIds.length === 0) return;
    let active = true;
    setLoading(true);
    setError(null);
    loadSummary(range, selectedPlayerIds)
      .then((data) => active && setSummary(data))
      .catch((reason: Error) => active && setError(reason.message))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [directoryLoaded, range, selectedPlayerIds, players.length]);

  const title = useMemo(() => {
    const pool = pools.find((item) => item.id === activePoolId);
    if (pool) return pool.name;
    if (selectedPlayerIds.length === 1) {
      return players.find((player) => player.id === selectedPlayerIds[0])?.name ?? "Jogador";
    }
    return "Lobos Performance";
  }, [activePoolId, players, pools, selectedPlayerIds]);

  const subtitle = `${selectedPlayerIds.length === 1 ? "Individual" : "Seleção"} · ${selectedPlayerIds.length} ${selectedPlayerIds.length === 1 ? "jogador" : "jogadores"} · ${range.from.split("-").reverse().join("/")} a ${range.to.split("-").reverse().join("/")}`;
  const series = summary.chart;

  const changePool = (poolId: string) => {
    setActivePoolId(poolId);
    const pool = pools.find((item) => item.id === poolId);
    if (pool) setSelectedPlayerIds(pool.playerIds);
  };

  const savePool = async (name: string) => {
    setLoading(true);
    try {
      const pool = await createPool(name, players, selectedPlayerIds);
      setPools((current) => [...current, pool]);
      setActivePoolId(pool.id);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Não foi possível salvar a pool.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-shell">
      <header className="topbar">
        <Brand compact />
        <div className="topbar__right">
          {!isSupabaseConfigured && <span className="demo-badge">Modo demonstração</span>}
          {isSupabaseConfigured && (
            <button className="icon-button" onClick={() => supabase?.auth.signOut()} title="Sair">
              <LogOut size={18} />
            </button>
          )}
        </div>
      </header>

      <main className="dashboard">
        <section className="dashboard__intro">
          <span className="eyebrow">Painel de performance MTT</span>
          <h1>{title}</h1>
          <p>{subtitle}</p>
        </section>

        <Filters
          range={range}
          players={players}
          pools={pools}
          selectedPlayerIds={selectedPlayerIds}
          activePoolId={activePoolId}
          busy={loading}
          onRangeChange={setRange}
          onPlayersChange={(ids) => {
            setSelectedPlayerIds(ids);
            setActivePoolId("");
          }}
          onPoolChange={changePool}
          onSavePool={savePool}
        />

        {error && <div className="error-banner">{error}</div>}

        <section className={`metrics-grid ${loading ? "is-loading" : ""}`}>
          {loading && (
            <div className="loading-overlay">
              <LoaderCircle className="spin" size={28} />
            </div>
          )}
          <MetricCard
            label="ROI"
            value={`${percentage.format(summary.roi)}%`}
            color="#f5be00"
            icon={Percent}
            series={series.map((point) => point.roi)}
          />
          <MetricCard
            label="Lucro"
            value={usd.format(summary.profitUsd)}
            color="#00d8b4"
            icon={CircleDollarSign}
            series={series.map((point) => point.profit)}
          />
          <MetricCard
            label="Torneios"
            value={integer.format(summary.tournaments)}
            color="#94a3b8"
            icon={Trophy}
            series={series.map((point) => point.volume)}
          />
          <MetricCard
            label="ITM*"
            value={`${percentage.format(summary.itm)}%`}
            color="#20e0d3"
            icon={Target}
            series={series.map((point) => point.itm)}
          />
          <MetricCard
            label="ABI"
            value={usd.format(summary.abi)}
            color="#4f83ff"
            icon={Gauge}
            series={series.map((point) => point.abi)}
          />
          <MetricCard
            label="US$/hora"
            value={summary.usdPerHour === null ? "Dados indisponíveis" : usd.format(summary.usdPerHour)}
            color="#22dec7"
            icon={Zap}
            muted={summary.usdPerHour === null}
          />
        </section>

        <PerformanceChart data={summary.chart} />
        <p className="data-note">
          * ITM segue a disponibilidade da fonte importada. Valores exibidos em dólar americano.
        </p>
      </main>
    </div>
  );
}
